import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Box, useTheme, Tooltip, IconButton, useMediaQuery } from '@mui/material';
import { BarChart, GetApp } from '@mui/icons-material';

import { WsEventsCases, WsRoomsCases } from '@bandido/shared/constants';
import { QueryKeys, SortingOptions } from '@frontend/features/common/types';
import { OrderOptionsValues, PagedResponse } from '@bandido/shared/dtos';
import { CaseResponseDto } from '@bandido/shared/dtos';
import { OriginatedByChannel } from '@bandido/shared/types';

import { usePagination } from '@frontend/features/common/components/Table';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { useCaseSavedFilters, useGetAllCases, useExportCasesCsv } from '@frontend/features/cases/queries';
import { getArrayParam } from '@frontend/features/common/helper';
import { useCases } from '@frontend/features/cases/components/CaseContext';
import { CaseListFilersOptions } from '@frontend/features/cases/components/CaseListFiltersOptions';
import { useSessionFilters } from '@frontend/features/cases/hooks/useSessionFilters';
import { useShowMineFilter } from '@frontend/features/common/hooks/useShowMineFilter';
import { CasesListDesktop } from '@frontend/features/cases/components/CasesListDesktop';
import { CasesListMobile } from '@frontend/features/cases/components/CasesListMobile';
import { parseLegacyDateFilters, parseDirectDateFilters } from '@frontend/features/cases/utils/dateFilters';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import { CONFIG } from '@frontend/config';

const replaceItem = (
  result: {
    data: PagedResponse<CaseResponseDto>;
  },
  newItem: CaseResponseDto,
) => {
  const { data } = result;
  const newResult = { ...result };
  const itemIndex = data.items.findIndex((item) => item.id === newItem.id);

  if (itemIndex !== -1) {
    newResult.data.items[itemIndex] = newItem;
  }

  return newResult;
};

export const CasesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isConnected, socketRef, sendMessage } = useWS(`${CONFIG.WS_URL}/cases`);
  const queryClient = useQueryClient();
  const pagination = usePagination();
  const { toggleModal } = useCases();
  const { addToast } = useSnackBar();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const sort = searchParams.get('sort') || 'updatedAt';
  const order = searchParams.get('order') || 'DESC';
  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);

  const { updateFilters, clearFilters } = useSessionFilters(searchParams, setSearchParams);

  const showMineFilter = useShowMineFilter({
    filterField: 'agentIds',
    label: t('filters.myCases'),
    tooltip: t('filters.myCases'),
    searchParams,
    updateFilters,
  });

  const exportCsvMutation = useExportCasesCsv();

  // Sincronizar el estado search con los searchParams
  useEffect(() => {
    setSearch(searchParams.get('search') || '');
  }, [searchParams]);

  const updateSort = (sortOptions: SortingOptions) => {
    updateFilters({ sort: sortOptions.sort, order: sortOptions.order });
  };

  const handleClearFilters = () => {
    showMineFilter.clearFilter();
    clearFilters();
  };

  const handleOpenStatistics = () => {
    setIsStatisticsModalOpen(true);
  };

  const handleCloseStatistics = () => {
    setIsStatisticsModalOpen(false);
  };

  const handleExportCsv = () => {
    exportCsvMutation.mutate(
      {
        search,
        sort,
        order: order as OrderOptionsValues,
        ...standardFilters,
        ...dateFilters,
        customFieldFilters,
        customDateFieldFilters,
      },
      {
        onSuccess: () => {
          addToast({
            message: t('cases.export.processingMessage'),
            color: 'success',
          });
        },
        onError: () => {
          addToast({
            message: t('cases.export.errorMessage'),
            color: 'error',
          });
        },
      }
    );
  };

  const statisticsButton = (
    <Tooltip title={t('cases.statistics.title')} arrow>
      <IconButton
        onClick={handleOpenStatistics}
        aria-label="Statistics"
        sx={{ 
          height: theme.spacing(4),
          width: theme.spacing(4),
          color: theme.palette.primary.main,
          borderRadius: theme.spacing(0.5),
          '&:hover': {
            color: theme.palette.primary.dark,
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <BarChart fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const exportCsvButton = (
    <Tooltip title={t('cases.export.csvButton')} arrow>
      <IconButton
        onClick={handleExportCsv}
        disabled={exportCsvMutation.isLoading}
        aria-label="Export CSV"
        sx={{ 
          height: theme.spacing(4),
          width: theme.spacing(4),
          color: theme.palette.primary.main,
          borderRadius: theme.spacing(0.5),
          '&:hover': {
            color: theme.palette.primary.dark,
          },
          transition: 'all 0.2s ease-in-out',
          '&:disabled': {
            opacity: 0.6,
          },
        }}
      >
        <GetApp fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const { data: filtersList } = useCaseSavedFilters({});

  const standardFilters = {
    status: getArrayParam(searchParams.get('status')),
    teamIds: getArrayParam(searchParams.get('teamIds'))?.map((i) => 
      i === 'notselected' ? 'notselected' : Number(i)
    ) as any,
    workflowIds: getArrayParam(searchParams.get('workflowIds')),
    stepIds: getArrayParam(searchParams.get('stepIds')),
    clientIds: getArrayParam(searchParams.get('clientIds')),
    agentIds: getArrayParam(searchParams.get('agentIds')),
    originatedByChannels: getArrayParam(searchParams.get('originatedByChannel')) as OriginatedByChannel[],
  };

  const urlPage = searchParams.get('page') || '1';

  // Procesar filtros de fecha - primero intenta filtros legacy, luego directos
  const legacyDateFilters = parseLegacyDateFilters(searchParams);
  const directDateFilters = parseDirectDateFilters(searchParams);
  const dateFilters = { ...legacyDateFilters, ...directDateFilters };

  // Filtros para campos personalizados, excluyendo filtros de fecha procesados
  const dateFilterKeys = ['createdDateFilter', 'updatedDateFilter', 'createdCustomDays', 'updatedCustomDays', 
    'createdSpecificDate', 'updatedSpecificDate', 'createdDateFrom', 'createdDateTo', 'updatedDateFrom', 
    'updatedDateTo', 'createdAfter', 'createdBefore', 'updatedAfter', 'updatedBefore'];
  
  // Procesar custom date field filters
  const customDateFieldFilters: { fieldName: string; after?: string; before?: string }[] = [];
  const customDateFieldKeys = new Set<string>();
  
  // Buscar parámetros con patrón fieldId_fieldName_after y fieldId_fieldName_before
  for (const [key, value] of searchParams.entries()) {
    const afterMatch = key.match(/^(.+)_after$/);
    const beforeMatch = key.match(/^(.+)_before$/);
    
    if (afterMatch && value) {
      const fieldName = afterMatch[1];
      customDateFieldKeys.add(fieldName);
      let existingFilter = customDateFieldFilters.find(f => f.fieldName === fieldName);
      if (!existingFilter) {
        existingFilter = { fieldName };
        customDateFieldFilters.push(existingFilter);
      }
      existingFilter.after = value;
    } else if (beforeMatch && value) {
      const fieldName = beforeMatch[1];
      customDateFieldKeys.add(fieldName);
      let existingFilter = customDateFieldFilters.find(f => f.fieldName === fieldName);
      if (!existingFilter) {
        existingFilter = { fieldName };
        customDateFieldFilters.push(existingFilter);
      }
      existingFilter.before = value;
    }
  }
  
  // Agregar todas las claves de custom date fields a los filtros excluidos
  const customDateFieldFilterKeys = Array.from(customDateFieldKeys).flatMap(fieldName => [
    `${fieldName}_after`,
    `${fieldName}_before`,
    `${fieldName}_filter`,
    `${fieldName}_customDays`,
    `${fieldName}_specificDate`,
    `${fieldName}_dateFrom`,
    `${fieldName}_dateTo`
  ]);
  
  const allDateFilterKeys = [...dateFilterKeys, ...customDateFieldFilterKeys];
  
  const customFieldFilters = Array.from(searchParams.entries())
    .filter(
      ([key]) => !Object.keys(standardFilters).includes(key) && 
                 !allDateFilterKeys.includes(key) && 
                 key !== 'search' && key !== 'sort' && key !== 'order' && key !== 'page',
    )
    .map(([fieldName, value]) => ({ fieldName, value }));

  const { data, isLoading } = useGetAllCases({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    sort,
    search,
    order: order as OrderOptionsValues,
    ...standardFilters,
    ...dateFilters,
    customFieldFilters,
    customDateFieldFilters,
  });

  useEffect(() => {
    if (isConnected) {
      sendMessage('joinRoom', WsRoomsCases.CASES);
    }
    socketRef.current?.on(WsEventsCases.CASE_CREATED, (createdData: string) => {
      const newItem = JSON.parse(createdData) as CaseResponseDto;
      const queries = queryClient.getQueryCache().findAll({ queryKey: [QueryKeys.getAllCases] });

      queries.forEach(({ queryKey }) => {
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (oldData) {
            const newData = oldData.data as PagedResponse<CaseResponseDto>;
            return {
              ...oldData,
              items: [newItem, ...newData.items],
              count: newData.count + 1,
            };
          }
        });
      });
    });
    socketRef.current?.on(WsEventsCases.CASE_UPDATED, (caseData: string) => {
      const updateItem = JSON.parse(caseData) as CaseResponseDto;
      const queries = queryClient.getQueryCache().findAll({ queryKey: [QueryKeys.getAllCases] });

      queries.forEach(({ queryKey }) => {
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (oldData) {
            return replaceItem(oldData, updateItem);
          }
        });
      });
    });

    return () => {
      socketRef.current?.off(WsEventsCases.CASE_CREATED);
      socketRef.current?.off(WsEventsCases.CASE_UPDATED);
    };
  }, [sendMessage, isConnected]);

  const filtersOptions = {
    form: (
      <CaseListFilersOptions
        title="Filtros"
        searchParams={searchParams}
        updateFilters={updateFilters}
        clearFilters={clearFilters}
      />
    ),
    filtersList: filtersList,
    filterListKey: 'caseslist',
    searchParams,
    setSearchParams,
    onClearFilters: handleClearFilters,
  };

  const totalFilters = useMemo(() => {
    const keysToOmit = ['sort', 'order', 'page', 'search'];
    let count = 0;

    searchParams.forEach((value, key) => {
      if (!keysToOmit.includes(key)) {
        const values = value
          .split('|')
          .filter(Boolean)
          .filter((opt) => opt !== 'null' || opt !== null);
        count += values.length;
      }
    });

    return count;
  }, [searchParams]);

  useEffect(() => {
    pagination.handlePagination(Number(urlPage), true);
  }, [searchParams, urlPage]);



  // Shared props for both desktop and mobile components
  const sharedProps = {
    title: t('cases.list.title'),
    subtitle: t('cases.list.subtitle'),
    pagination,
    filtersCount: totalFilters,
    filtersOptions,
    onSearchChange: (value: string) => {
      setSearch(value);
      updateFilters({ search: value });
    },
    sorting: {
      options: { sort, order: order as OrderOptionsValues },
      handler: updateSort,
    },
    onCreate: toggleModal,
    actionToggles: showMineFilter.actionToggle ? [showMineFilter.actionToggle] : undefined,
    statisticsButton: (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {exportCsvButton}
        {statisticsButton}
      </Box>
    ),
    isLoading,
    // Pass raw data items and total count
    items: data?.items || [],
    totalCount: data?.count || 0,
    isStatisticsModalOpen,
    onCloseStatistics: handleCloseStatistics,
  };

  return isMobile ? (
    <CasesListMobile {...sharedProps} />
  ) : (
    <CasesListDesktop {...sharedProps} />
  );
};
